import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyModal({
  isOpen,
  onClose
}: PrivacyModalProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  return <AnimatePresence data-id="element-218">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-219">
            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} onClick={e => e.stopPropagation()} className="bg-[var(--bg-card)] border border-[var(--accent-amber)]/20 w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden relative" data-id="element-220">
              {/* Header */}
              <div className="p-6 border-b border-[var(--bg-card-hover)] flex items-center justify-between bg-[var(--bg-primary)]" data-id="element-221">
                <h2 className="text-2xl font-serif text-[var(--text-primary)]" data-id="element-222">
                  Política de Privacidad
                </h2>
                <button onClick={onClose} className="p-2 hover:bg-[var(--bg-card-hover)] rounded-full transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]" aria-label="Cerrar" data-id="element-223">
                  <X className="w-6 h-6" data-id="element-224" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-6 overflow-y-auto text-[var(--text-secondary)] space-y-6 text-sm leading-relaxed custom-scrollbar" data-id="element-225">
                <section data-id="element-226">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2" data-id="element-227">
                    1. Introducción
                  </h3>
                  <p data-id="element-228">
                    Bienvenido a 3AM Club. Nos tomamos muy en serio la
                    privacidad de tus datos. Esta Política de Privacidad
                    describe cómo recopilamos, usamos y protegemos tu
                    información personal cuando utilizas nuestro sitio web y
                    servicios.
                  </p>
                </section>

                <section data-id="element-229">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2" data-id="element-230">
                    2. Recopilación de Información
                  </h3>
                  <p data-id="element-231">
                    Podemos recopilar información personal que nos proporcionas
                    voluntariamente, como tu nombre y dirección de correo
                    electrónico al registrarte o realizar una compra. También
                    recopilamos datos técnicos automáticamente, como tu
                    dirección IP, tipo de navegador y comportamiento de
                    navegación a través de cookies y tecnologías similares.
                  </p>
                </section>

                <section data-id="element-232">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2" data-id="element-233">
                    3. Uso de la Información
                  </h3>
                  <p data-id="element-234">Utilizamos tu información para:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1" data-id="element-235">
                    <li data-id="element-236">
                      Procesar tus pedidos y entregarte el contenido digital.
                    </li>
                    <li data-id="element-237">
                      Mejorar nuestro sitio web y la experiencia del usuario.
                    </li>
                    <li data-id="element-238">
                      Enviarte actualizaciones importantes sobre tu compra.
                    </li>
                    <li data-id="element-239">
                      Cumplir con obligaciones legales y prevenir fraudes.
                    </li>
                  </ul>
                </section>

                <section data-id="element-240">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2" data-id="element-241">
                    4. Cookies y Tecnologías de Rastreo
                  </h3>
                  <p data-id="element-242">
                    Utilizamos cookies para mejorar la funcionalidad del sitio y
                    analizar el tráfico. Puedes configurar tu navegador para
                    rechazar todas las cookies o para que te avise cuando se
                    envíe una cookie. Sin embargo, algunas funciones del sitio
                    pueden no funcionar correctamente sin ellas.
                  </p>
                </section>

                <section data-id="element-243">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2" data-id="element-244">
                    5. Compartir Información con Terceros
                  </h3>
                  <p data-id="element-245">
                    No vendemos ni alquilamos tu información personal a
                    terceros. Podemos compartir datos con proveedores de
                    servicios de confianza que nos ayudan a operar nuestro
                    negocio (como procesadores de pagos), siempre bajo estrictos
                    acuerdos de confidencialidad.
                  </p>
                </section>

                <section data-id="element-246">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2" data-id="element-247">
                    6. Seguridad de los Datos
                  </h3>
                  <p data-id="element-248">
                    Implementamos medidas de seguridad razonables para proteger
                    tu información contra acceso no autorizado, alteración o
                    destrucción. Sin embargo, ninguna transmisión por Internet
                    es 100% segura.
                  </p>
                </section>

                <section data-id="element-249">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2" data-id="element-250">
                    7. Tus Derechos
                  </h3>
                  <p data-id="element-251">
                    Tienes derecho a acceder, corregir o eliminar tu información
                    personal. También puedes oponerte al procesamiento de tus
                    datos o solicitar su portabilidad.
                  </p>
                </section>

                <section data-id="element-252">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2" data-id="element-253">
                    8. Cambios en esta Política
                  </h3>
                  <p data-id="element-254">
                    Podemos actualizar esta Política de Privacidad
                    ocasionalmente. Te notificaremos sobre cambios
                    significativos publicando la nueva política en esta página.
                  </p>
                </section>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-[var(--bg-card-hover)] bg-[var(--bg-primary)] flex justify-end" data-id="element-255">
                <button onClick={onClose} className="px-4 py-2 bg-[var(--bg-card-hover)] hover:bg-[var(--accent-amber)] hover:text-[#070b14] text-[var(--text-primary)] rounded-lg transition-colors text-sm font-medium" data-id="element-256">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}